import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import FileAttachments from '../components/FileAttachments';
import { AlertTriangle, Plus, XCircle, CheckCircle2, ChevronDown, ChevronUp, Paperclip, Send, RotateCcw, Pencil, Trash2, AlertCircle } from 'lucide-react';
import { BadgeEstadoFirma, ModalPasarPdteFirma, ModalRevertirBorrador, esEstadoFirmado, IconoBloqueado } from '../components/FlujoFirmaAcciones';
import { ModalEliminarRegistro, esAdmin } from '../components/RegistroAcciones';

// ═══════════════════════════════════════════════════════════════
// MODAL: Crear nueva NC
// ═══════════════════════════════════════════════════════════════
function ModalNC({ onClose, onGuardado }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ fecha: today, descripcion: '', producto_afectado: '', lote: '', riesgo: 'bajo', accion_correctiva: '', accion_preventiva: '', responsable_nombre: '', fecha_cierre: '', observaciones: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.descripcion) { setError('La descripción es obligatoria'); return; }
    setLoading(true);
    try { const r = await api.post('/registros/no-conformidades', form); setSavedId(r.data.id); }
    catch (e) { setError(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><AlertTriangle size={18} color="var(--color-primary)" />Nueva No Conformidad / CAPA</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {savedId && <div className="alert alert-success"><CheckCircle2 size={16} /> Guardado en estado Borrador. Adjunta evidencias o documentación.</div>}
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Fecha *</label><input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Nivel de riesgo</label>
              <select className="form-control" value={form.riesgo} onChange={e => set('riesgo', e.target.value)} disabled={!!savedId}>
                <option value="bajo">🟢 Bajo</option><option value="medio">🟡 Medio</option>
                <option value="alto">🟠 Alto</option><option value="critico">🔴 Crítico</option>
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Descripción *</label><textarea className="form-control" rows={4} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="Describe la no conformidad detectada..." disabled={!!savedId} /></div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Producto afectado</label><input className="form-control" value={form.producto_afectado} onChange={e => set('producto_afectado', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Lote</label><input className="form-control" value={form.lote} onChange={e => set('lote', e.target.value)} disabled={!!savedId} /></div>
          </div>
          <div className="form-group"><label className="form-label">Acción correctiva</label><textarea className="form-control" rows={3} value={form.accion_correctiva} onChange={e => set('accion_correctiva', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-group"><label className="form-label">Acción preventiva</label><textarea className="form-control" rows={3} value={form.accion_preventiva} onChange={e => set('accion_preventiva', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Responsable</label><input className="form-control" value={form.responsable_nombre} onChange={e => set('responsable_nombre', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Fecha cierre prevista</label><input type="date" className="form-control" value={form.fecha_cierre} onChange={e => set('fecha_cierre', e.target.value)} disabled={!!savedId} /></div>
          </div>
          <div className="form-group"><label className="form-label">Observaciones</label><textarea className="form-control" rows={2} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} disabled={!!savedId} /></div>
          <FileAttachments seccion="no-conformidades" entidadTipo="no_conformidad" entidadId={savedId} label="Adjuntar evidencias / documentación" />
        </div>
        <div className="modal-footer">
          {!savedId ? (
            <><button className="btn btn-outline" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardar} disabled={loading}>
                {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />} Guardar
              </button></>
          ) : (
            <><button className="btn btn-outline" onClick={onClose}>Cerrar</button>
              <button className="btn btn-primary" onClick={onGuardado}><CheckCircle2 size={16} /> Finalizar</button></>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Editar NC (admin + contraseña)
// ═══════════════════════════════════════════════════════════════
function ModalEditarNC({ registro, onClose, onActualizado }) {
  const [form, setForm] = useState({
    fecha: registro.fecha, descripcion: registro.descripcion || '',
    producto_afectado: registro.producto_afectado || '', lote: registro.lote || '',
    riesgo: registro.riesgo || 'bajo', accion_correctiva: registro.accion_correctiva || '',
    accion_preventiva: registro.accion_preventiva || '',
    responsable_nombre: registro.responsable_nombre || '',
    fecha_cierre: registro.fecha_cierre || '', observaciones: registro.observaciones || ''
  });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGuardar = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/registros/no-conformidades/${registro.id}`, { ...form, password });
      onActualizado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={20} color="#3b82f6" /> Editar No Conformidad
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
            <AlertCircle size={16} /> <strong>Acción restringida:</strong> Solo administradores. Se requiere contraseña.
          </div>

          <div className="form-grid">
            <div className="form-group"><label className="form-label">Fecha</label>
              <input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Nivel de riesgo</label>
              <select className="form-control" value={form.riesgo} onChange={e => set('riesgo', e.target.value)}>
                <option value="bajo">🟢 Bajo</option><option value="medio">🟡 Medio</option>
                <option value="alto">🟠 Alto</option><option value="critico">🔴 Crítico</option>
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Descripción</label>
            <textarea className="form-control" rows={3} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} /></div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Producto afectado</label>
              <input className="form-control" value={form.producto_afectado} onChange={e => set('producto_afectado', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Lote</label>
              <input className="form-control" value={form.lote} onChange={e => set('lote', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Acción correctiva</label>
            <textarea className="form-control" rows={2} value={form.accion_correctiva} onChange={e => set('accion_correctiva', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Acción preventiva</label>
            <textarea className="form-control" rows={2} value={form.accion_preventiva} onChange={e => set('accion_preventiva', e.target.value)} /></div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Responsable</label>
              <input className="form-control" value={form.responsable_nombre} onChange={e => set('responsable_nombre', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Fecha cierre</label>
              <input type="date" className="form-control" value={form.fecha_cierre} onChange={e => set('fecha_cierre', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Observaciones</label>
            <textarea className="form-control" rows={2} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} /></div>

          <div className="form-group" style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
            <label className="form-label" style={{ color: '#92400e', fontWeight: 600 }}>🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGuardar} disabled={loading || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FILA
// ═══════════════════════════════════════════════════════════════
function FilaNC({ r, isAdmin, onPasarPdte, onRevertir, onEditar, onEliminar }) {
  const [expanded, setExpanded] = useState(false);
  const riesgoCls = { bajo: 'badge-firmado', medio: 'badge-pendiente_firma', alto: 'badge-rechazado', critico: 'badge-rechazado' };
  const estado = r.estado || 'borrador';
  const bloqueado = esEstadoFirmado(estado, false);

  return (
    <>
      <tr>
        <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--color-primary)', fontWeight: 700 }}>{r.numero_incidencia}</span></td>
        <td>{new Date(r.fecha).toLocaleDateString('es-ES')}</td>
        <td style={{ maxWidth: 240, fontWeight: 500 }}>{r.descripcion}</td>
        <td style={{ fontSize: '0.82rem' }}>{r.producto_afectado || '—'}</td>
        <td><span className={`badge ${riesgoCls[r.riesgo]}`}>{r.riesgo}</span></td>
        <td style={{ fontSize: '0.82rem' }}>{r.responsable_nombre}</td>
        <td><BadgeEstadoFirma estado={estado} /></td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FileAttachments seccion="no-conformidades" entidadTipo="no_conformidad" entidadId={r.id} compact />
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            
            {estado === 'borrador' && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onPasarPdte(r)} 
                title="Pasar a Pdte. firma" style={{ color: '#f59e0b' }}>
                <Send size={13} />
              </button>
            )}
            {estado === 'pendiente_firma' && isAdmin && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onRevertir(r)} 
                title="Revertir a Borrador (admin)" style={{ color: '#6b7280' }}>
                <RotateCcw size={13} />
              </button>
            )}
            {isAdmin && !bloqueado && (
              <>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEditar(r)} 
                  title="Editar (admin)" style={{ color: '#3b82f6' }}>
                  <Pencil size={13} />
                </button>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEliminar(r)} 
                  title="Eliminar (admin)" style={{ color: 'var(--color-danger)' }}>
                  <Trash2 size={13} />
                </button>
              </>
            )}
            {bloqueado && <IconoBloqueado estado={estado} />}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr><td colSpan={8} style={{ padding: '0 14px 14px', background: 'var(--color-gray-1)' }}>
          <FileAttachments seccion="no-conformidades" entidadTipo="no_conformidad" entidadId={r.id} label="Subir evidencias o documentación" />
        </td></tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// PÁGINA
// ═══════════════════════════════════════════════════════════════
export default function NoConformidadesPage() {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editarRegistro, setEditarRegistro] = useState(null);
  const [eliminarRegistro, setEliminarRegistro] = useState(null);
  const [pasarPdteRegistro, setPasarPdteRegistro] = useState(null);
  const [revertirRegistro, setRevertirRegistro] = useState(null);
  const [success, setSuccess] = useState('');
  const isAdmin = esAdmin();

  const load = () => { setLoading(true); api.get('/registros/no-conformidades').then(r => setRegistros(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const mostrarSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><AlertTriangle size={24} color="var(--color-primary)" />No Conformidades / CAPA</h1>
          <p>{registros.length} incidencias · Cada NC se firma individualmente por la Directora Técnica</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={16} /> Nueva Incidencia</button>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}><CheckCircle2 size={16} /> {success}</div>}

      <div className="card">
        {loading ? <div className="loading-overlay"><div className="spinner" /></div> :
          registros.length === 0 ? <div className="empty-state"><AlertTriangle size={48} /><h3>Sin no conformidades</h3></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Nº</th><th>Fecha</th><th>Descripción</th><th>Producto</th><th>Riesgo</th><th>Responsable</th><th>Estado</th><th><Paperclip size={13} /></th></tr></thead>
                <tbody>{registros.map(r => 
                  <FilaNC key={r.id} r={r} isAdmin={isAdmin}
                    onPasarPdte={setPasarPdteRegistro}
                    onRevertir={setRevertirRegistro}
                    onEditar={setEditarRegistro}
                    onEliminar={setEliminarRegistro} />)}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal && <ModalNC onClose={() => setModal(false)} onGuardado={() => { setModal(false); load(); }} />}
      
      {pasarPdteRegistro && <ModalPasarPdteFirma 
        endpoint={`/registros/no-conformidades/${pasarPdteRegistro.id}/pasar-pdte-firma`}
        descripcion={`${pasarPdteRegistro.numero_incidencia} · ${(pasarPdteRegistro.descripcion || '').substring(0, 60)}`}
        onClose={() => setPasarPdteRegistro(null)}
        onConfirmado={() => { setPasarPdteRegistro(null); load(); mostrarSuccess('Marcado como pendiente de firma. Ve a Firmas Pendientes.'); }} />}
      
      {revertirRegistro && <ModalRevertirBorrador 
        endpoint={`/registros/no-conformidades/${revertirRegistro.id}/reset-estado`}
        descripcion={`${revertirRegistro.numero_incidencia} · ${(revertirRegistro.descripcion || '').substring(0, 60)}`}
        onClose={() => setRevertirRegistro(null)}
        onConfirmado={() => { setRevertirRegistro(null); load(); mostrarSuccess('Revertido a borrador'); }} />}
      
      {editarRegistro && <ModalEditarNC registro={editarRegistro} 
        onClose={() => setEditarRegistro(null)}
        onActualizado={() => { setEditarRegistro(null); load(); mostrarSuccess('Incidencia actualizada'); }} />}
      
      {eliminarRegistro && <ModalEliminarRegistro 
        endpoint={`/registros/no-conformidades/${eliminarRegistro.id}`}
        descripcion={`${eliminarRegistro.numero_incidencia} · ${(eliminarRegistro.descripcion || '').substring(0, 60)}`}
        onClose={() => setEliminarRegistro(null)}
        onEliminado={() => { setEliminarRegistro(null); load(); mostrarSuccess('Incidencia eliminada'); }} />}
    </div>
  );
}
